import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  type BoxProps,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { getIn, FormikProps } from 'formik';
import { useAtomValue } from 'jotai';
import { forwardRef, useState, type ComponentPropsWithoutRef, type KeyboardEvent } from 'react';
import { SectionUnitsEnum, TemplateFieldTypeEnum, UnitSystemEnum } from '@/__generated__/graphql';
import * as globalState from '@/components/Analysis/state';
import type { QaqcKpiTemplate, QaqcKpiTemplateField } from '../types';
import { buildFieldByKey, computeQaqcEntryDerivedValues } from './utils';
import type { QaqcFormValues } from './utils';
import { getSectionUnitLabel, getSectionUnitSymbol } from '../utils';
import {
  qaQcMultiValueAutocompleteSx,
  qaQcMultiValueChipDeleteIconWrapperSx,
  qaQcMultiValueChipSx,
  qaQcMultiValueClearIndicatorSx,
} from '../components/Panel/styles';
import { QAQC_NUMERIC_HELPER, isValidQaqcNumericString } from './qaqcFormValidation';

const numericInvalidAttemptHelper = QAQC_NUMERIC_HELPER;

const preventNonNumericKeystroke = (event: KeyboardEvent<HTMLInputElement>) => {
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return;
  }
  const allowedNavigation = new Set([
    ' ',
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ]);
  if (allowedNavigation.has(event.key)) {
    return;
  }
  if (event.key >= '0' && event.key <= '9') {
    return;
  }
  if (event.key === '.') {
    const { value } = event.currentTarget;
    if (!value.includes('.')) {
      return;
    }
  }
  if (event.key === '-') {
    const { value, selectionStart } = event.currentTarget;
    const isAtStart = selectionStart === 0;
    if (isAtStart && !value.includes('-')) {
      return;
    }
  }
  event.preventDefault();
};

const sanitizeNumericInput = (raw: string) => {
  // Allow one leading '-' and a single '.', drop everything else.
  const trimmedStart = raw.trimStart();
  const hasLeadingMinus = trimmedStart.startsWith('-');
  const normalized = raw.replaceAll(/[^\d.]/g, '');
  const dotIndex = normalized.indexOf('.');
  if (dotIndex === -1) {
    if (hasLeadingMinus && normalized === '') {
      return '-';
    }
    return hasLeadingMinus && normalized !== '' ? `-${normalized}` : normalized;
  }
  const before = normalized.slice(0, dotIndex + 1);
  const after = normalized.slice(dotIndex + 1).replaceAll('.', '');
  const combined = `${before}${after}`;
  if (hasLeadingMinus && combined === '.') {
    return '-.';
  }
  if (hasLeadingMinus && combined === '') {
    return '-';
  }
  return hasLeadingMinus && combined !== '' ? `-${combined}` : combined;
};

const sanitizeForField = (field: QaqcKpiTemplateField, raw: string) => {
  if (field.type === TemplateFieldTypeEnum.Numeric) {
    return sanitizeNumericInput(raw);
  }
  return raw;
};

const labelWithUnit = (
  name: string,
  fieldUnit: SectionUnitsEnum | null | undefined,
  unitSystem: UnitSystemEnum
) => {
  if (!fieldUnit || fieldUnit === SectionUnitsEnum.Na) {
    return name;
  }
  const symbol = getSectionUnitSymbol(fieldUnit, unitSystem);
  if (symbol) {
    return `${name} (${symbol})`;
  }
  const unitLabel = getSectionUnitLabel(fieldUnit);
  return unitLabel ? `${name} (${unitLabel})` : name;
};

type Props = {
  template: QaqcKpiTemplate;
  formik: FormikProps<QaqcFormValues>;
  fieldKey: string;
  tableIndex: number;
  entryIndex: number;
  disabled?: boolean;
};

type ChipDeleteIconProps = ComponentPropsWithoutRef<'span'> & Pick<BoxProps, 'sx'>;

const ChipDeleteIcon = forwardRef<HTMLSpanElement, ChipDeleteIconProps>(
  ({ sx: incomingSx, ...rest }, reference) => (
    <Box
      component="span"
      ref={reference}
      {...rest}
      sx={
        [
          qaQcMultiValueChipDeleteIconWrapperSx,
          ...(incomingSx ? [incomingSx] : []),
        ] as SxProps<Theme>
      }
    >
      <CloseIcon sx={{ fontSize: 10, pointerEvents: 'none' }} />
    </Box>
  )
);
ChipDeleteIcon.displayName = 'ChipDeleteIcon';

const MultiValueField = ({
  field,
  label,
  list,
  options,
  errorText,
  onBlur,
  onCommit,
  disabled,
}: {
  field: QaqcKpiTemplateField;
  label: string;
  list: string[];
  options: string[];
  errorText: string | undefined;
  onBlur: () => void;
  onCommit: (next: string[]) => void;
  disabled?: boolean;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [localHelperText, setLocalHelperText] = useState<string | undefined>(undefined);
  const isFreeSolo = field.type !== TemplateFieldTypeEnum.MultiSelect;
  const blockBadKeys =
    field.type === TemplateFieldTypeEnum.Numeric ? preventNonNumericKeystroke : undefined;

  const commitFreeSoloToken = (token: string) => {
    const trimmed = sanitizeForField(field, token).trim();
    if (trimmed === '') {
      return;
    }
    onCommit([...list, trimmed]);
    setInputValue('');
    setLocalHelperText(undefined);
  };

  return (
    <Autocomplete
      multiple
      freeSolo={isFreeSolo}
      options={options}
      value={list}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'reset') {
          setInputValue('');
          setLocalHelperText(undefined);
          return;
        }
        const sanitized = sanitizeForField(field, newInputValue);
        setInputValue(sanitized);
        if (
          field.type === TemplateFieldTypeEnum.Numeric &&
          newInputValue !== sanitized &&
          newInputValue !== ''
        ) {
          setLocalHelperText(numericInvalidAttemptHelper);
        } else if (
          field.type === TemplateFieldTypeEnum.Numeric &&
          sanitized !== '' &&
          !isValidQaqcNumericString(sanitized)
        ) {
          setLocalHelperText(numericInvalidAttemptHelper);
        } else {
          setLocalHelperText(undefined);
        }
      }}
      disableCloseOnSelect
      clearOnBlur={false}
      filterSelectedOptions={false}
      isOptionEqualToValue={(option, value) => option === value}
      onChange={(_, newValue) => {
        const normalized = Array.isArray(newValue)
          ? newValue.map((item) => (typeof item === 'string' ? item : String(item)))
          : [];
        onCommit(normalized);
        setInputValue('');
      }}
      onBlur={() => {
        if (isFreeSolo && !disabled && inputValue.trim() !== '') {
          commitFreeSoloToken(inputValue);
        }
        onBlur();
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const tagProps = getTagProps({ index });
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { key, ...chipProps } = tagProps;
          return (
            <Chip
              {...chipProps}
              key={`qaqc-multivalue-${index}-${String(option)}`}
              label={option}
              size="small"
              deleteIcon={<ChipDeleteIcon />}
              sx={qaQcMultiValueChipSx}
            />
          );
        })
      }
      renderInput={(parameters) => {
        const { inputProps, ...textFieldProps } = parameters;
        return (
          <TextField
            {...textFieldProps}
            label={label}
            error={Boolean(errorText)}
            helperText={errorText ?? localHelperText}
            size="small"
            variant="outlined"
            slotProps={{
              htmlInput: {
                ...inputProps,
                onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
                  blockBadKeys?.(event);
                  if (event.defaultPrevented) {
                    if (field.type === TemplateFieldTypeEnum.Numeric) {
                      setLocalHelperText(numericInvalidAttemptHelper);
                    }
                    return;
                  }
                  if (
                    isFreeSolo &&
                    !disabled &&
                    field.type === TemplateFieldTypeEnum.Numeric &&
                    (event.key === ' ' || event.code === 'Space')
                  ) {
                    event.preventDefault();
                    event.stopPropagation();
                    commitFreeSoloToken(event.currentTarget.value);
                    return;
                  }
                  inputProps.onKeyDown?.(event);
                  if (event.defaultPrevented) {
                    return;
                  }
                  if (event.key === 'Enter') {
                    event.stopPropagation();
                    if (isFreeSolo && !disabled) {
                      event.preventDefault();
                      commitFreeSoloToken(event.currentTarget.value);
                    }
                    return;
                  }
                  if (event.key === ',' && isFreeSolo && !disabled) {
                    event.preventDefault();
                    event.stopPropagation();
                    commitFreeSoloToken(event.currentTarget.value);
                  }
                },
              },
            }}
          />
        );
      }}
      disabled={disabled}
      sx={qaQcMultiValueAutocompleteSx}
      slotProps={{
        clearIndicator: {
          sx: qaQcMultiValueClearIndicatorSx,
        },
      }}
    />
  );
};

const SingleSelectField = ({
  fieldKey,
  field,
  label,
  value,
  errorText,
  onBlur,
  onCommit,
  disabled,
}: {
  fieldKey: string;
  field: QaqcKpiTemplateField;
  label: string;
  value: string;
  errorText: string | undefined;
  onBlur: () => void;
  onCommit: (next: string) => void;
  disabled?: boolean;
}) => (
  <FormControl fullWidth size="small" error={Boolean(errorText)}>
    <InputLabel id={`${fieldKey}-label`}>{label}</InputLabel>
    <Select
      labelId={`${fieldKey}-label`}
      label={label}
      value={value}
      onChange={(event) => onCommit(event.target.value)}
      onBlur={onBlur}
      disabled={disabled}
    >
      <MenuItem value="">
        <em>—</em>
      </MenuItem>
      {(field.options ?? []).map((option: string) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </Select>
    {errorText ? (
      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
        {errorText}
      </Typography>
    ) : null}
  </FormControl>
);

type ScalarOrList = string | string[] | undefined;

const multiValueList = (fieldValue: ScalarOrList): string[] => {
  if (Array.isArray(fieldValue)) {
    return fieldValue;
  }
  if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
    return [String(fieldValue)];
  }
  return [];
};

const PlainTextFields = ({
  field,
  fieldKey,
  fieldValue,
  label,
  errorText,
  onBlur,
  setValue,
  disabled,
}: {
  field: QaqcKpiTemplateField;
  fieldKey: string;
  fieldValue: ScalarOrList;
  label: string;
  errorText: string | undefined;
  onBlur: () => void;
  setValue: (next: string | string[]) => void;
  disabled?: boolean;
}) => {
  const [localHelperText, setLocalHelperText] = useState<string | undefined>(undefined);
  if (field.type === TemplateFieldTypeEnum.Numeric) {
    return (
      <TextField
        fullWidth
        size="small"
        type="text"
        label={label}
        value={typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : ''}
        onChange={(event) => {
          const raw = event.target.value;
          const sanitized = sanitizeNumericInput(raw);
          if (raw !== sanitized && raw !== '') {
            setLocalHelperText(numericInvalidAttemptHelper);
          } else if (sanitized !== '' && !isValidQaqcNumericString(sanitized)) {
            setLocalHelperText(numericInvalidAttemptHelper);
          } else {
            setLocalHelperText(undefined);
          }
          setValue(sanitized);
        }}
        onBlur={onBlur}
        error={Boolean(errorText)}
        helperText={errorText ?? localHelperText}
        disabled={disabled}
        variant="outlined"
        slotProps={{
          htmlInput: {
            inputMode: 'decimal',
            pattern: '[-]?[0-9]*[.]?[0-9]*',
            onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
              preventNonNumericKeystroke(event);
              if (event.defaultPrevented) {
                setLocalHelperText(numericInvalidAttemptHelper);
              }
            },
          },
        }}
      />
    );
  }

  if (field.type === TemplateFieldTypeEnum.Textarea) {
    return (
      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label={label}
        value={typeof fieldValue === 'string' ? fieldValue : ''}
        onChange={(event) => setValue(event.target.value)}
        onBlur={onBlur}
        error={Boolean(errorText)}
        helperText={errorText}
        disabled={disabled}
        variant="outlined"
      />
    );
  }

  const isTimeLike = fieldKey.includes('time') && field.type === TemplateFieldTypeEnum.Input;

  return (
    <TextField
      fullWidth
      size="small"
      type={isTimeLike ? 'time' : 'text'}
      label={label}
      value={typeof fieldValue === 'string' ? fieldValue : ''}
      onChange={(event) => setValue(event.target.value)}
      onBlur={onBlur}
      error={Boolean(errorText)}
      helperText={errorText}
      disabled={disabled}
      variant="outlined"
      slotProps={isTimeLike ? { inputLabel: { shrink: true } } : undefined}
    />
  );
};

const EditableField = ({
  field,
  fieldKey,
  fieldValue,
  label,
  errorText,
  onBlur,
  setValue,
  disabled,
}: {
  field: QaqcKpiTemplateField;
  fieldKey: string;
  fieldValue: ScalarOrList;
  label: string;
  errorText: string | undefined;
  onBlur: () => void;
  setValue: (next: string | string[]) => void;
  disabled?: boolean;
}) => {
  if (field.allowsMultipleValues || field.type === TemplateFieldTypeEnum.MultiSelect) {
    return (
      <MultiValueField
        field={field}
        label={label}
        list={multiValueList(fieldValue)}
        options={field.options?.length ? field.options : []}
        errorText={errorText}
        onBlur={onBlur}
        onCommit={(next) => setValue(next)}
        disabled={disabled}
      />
    );
  }

  if (field.type === TemplateFieldTypeEnum.SingleSelect && field.options?.length) {
    return (
      <SingleSelectField
        fieldKey={fieldKey}
        field={field}
        label={label}
        value={typeof fieldValue === 'string' ? fieldValue : ''}
        errorText={errorText}
        onBlur={onBlur}
        onCommit={(next) => setValue(next)}
        disabled={disabled}
      />
    );
  }

  return (
    <PlainTextFields
      field={field}
      fieldKey={fieldKey}
      fieldValue={fieldValue}
      label={label}
      errorText={errorText}
      onBlur={onBlur}
      setValue={setValue}
      disabled={disabled}
    />
  );
};

export const QaqcFieldControl = ({
  template,
  formik,
  fieldKey,
  tableIndex,
  entryIndex,
  disabled,
}: Props) => {
  const unitSystem = useAtomValue(globalState.unitSystem);
  const fieldByKey = buildFieldByKey(template);
  const field = fieldByKey.get(fieldKey);
  const relativePath = `tables.${tableIndex}.entries.${entryIndex}.values.${fieldKey}`;
  const fieldValue = getIn(formik.values, relativePath) as string | string[] | undefined;

  if (!field) {
    return null;
  }

  const label = labelWithUnit(field.name, field.fieldUnit, unitSystem);

  if (field.isDerived) {
    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        value={typeof fieldValue === 'string' ? fieldValue : ''}
        disabled
        variant="outlined"
      />
    );
  }

  const touched = getIn(formik.touched, relativePath);
  const err = getIn(formik.errors, relativePath);
  const submitCount = formik.submitCount ?? 0;
  const errorText = (touched || submitCount > 0) && typeof err === 'string' ? err : undefined;
  const valuesPath = `tables.${tableIndex}.entries.${entryIndex}.values`;
  const fieldKeysForTable = new Set(formik.values.tables[tableIndex]?.fieldKeys ?? []);

  const setValue = (next: string | string[]) => {
    const previous = getIn(formik.values, valuesPath) as
      | Record<string, string | string[]>
      | undefined;
    const merged: Record<string, string | string[]> = { ...previous, [fieldKey]: next };
    const derived = computeQaqcEntryDerivedValues(template, merged);
    const derivedForThisTable: Record<string, string> = {};
    for (const [derivedKey, derivedValue] of Object.entries(derived)) {
      if (fieldKeysForTable.has(derivedKey)) {
        derivedForThisTable[derivedKey] = derivedValue;
      }
    }
    void formik.setFieldValue(valuesPath, { ...merged, ...derivedForThisTable }).then(() => {
      void formik.validateField(relativePath);
    });
  };

  const onBlur = () => {
    void formik.setFieldTouched(relativePath, true);
  };

  return (
    <EditableField
      field={field}
      fieldKey={fieldKey}
      fieldValue={fieldValue}
      label={label}
      errorText={errorText}
      onBlur={onBlur}
      setValue={setValue}
      disabled={disabled}
    />
  );
};
